import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField, 
  InputAdornment, 
  Button, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton 
} from '@mui/material';
import { Filter, Key, RefreshCw, X as CloseIcon, Send } from 'lucide-react';
import { environment } from '../config/environment';
import { useI18n } from '../i18n';
import { getApiHeadersForGet } from '../utils/apiUtils';
import SendDigitalCodeDialog from '../components/SendDigitalCodeDialog';

interface PassCode {
  id: string;
  code: string;
  uses_remaining: number;
  max_uses: number;
  is_active: boolean;
  pass_id: string;
  created_at: string;
  used_at: string | null;
  code_kind?: string | null;
  recipient_email?: string | null;
  sent_at?: string | null;
}

interface ContestPassCodes {
  contest_id: string;
  contest_name: string;
  codes: PassCode[];
  total_codes: number;
  active_codes: number;
  used_codes: number;
  available_codes: number;
}

const ContestPassCodesPage: React.FC = () => {
  const { t } = useI18n();
  const [contestPassCodes, setContestPassCodes] = useState<ContestPassCodes[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedContest, setExpandedContest] = useState<string | null>(null);
  const [selectedCode, setSelectedCode] = useState<PassCode | null>(null);
  const [sendTarget, setSendTarget] = useState<{ code: PassCode; contestName: string } | null>(null);

  // Marca un código como enviado en el estado local (lista + modal abierto).
  const markCodeSent = (codeId: string, recipientEmail: string, sentAt: string) => {
    setContestPassCodes(prev => prev.map(cd => ({
      ...cd,
      codes: cd.codes.map(c => (c.id === codeId ? { ...c, recipient_email: recipientEmail, sent_at: sentAt } : c))
    })));
    setSelectedCode(prev => (prev && prev.id === codeId ? { ...prev, recipient_email: recipientEmail, sent_at: sentAt } : prev));
  };

  useEffect(() => {
    fetchPassCodes();
  }, []);

  const fetchPassCodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${environment.apiBaseUrl}/contest_passcodes`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching pass codes');
      }
      
      const data = await response.json();
      setContestPassCodes(data || []);
    } catch (error) {
      console.error('Error fetching pass codes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar si el término de búsqueda es una subsecuencia del texto
  const isSubsequence = (searchTerm: string, text: string): boolean => {
    if (!text || !searchTerm) return false;
    searchTerm = searchTerm.toLowerCase().trim();
    text = text.toLowerCase().trim();
    if (searchTerm.length === 0) return true;
    if (searchTerm.length > text.length) return false;
    
    let searchIndex = 0;
    for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
      if (text[i] === searchTerm[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === searchTerm.length;
  };

  // Filtrar Pass Codes según el término de búsqueda
  const filteredContestPassCodes = contestPassCodes.filter((contestData) => {
    const searchValue = searchTerm.trim();
    if (!searchValue) return true;
    
    // Buscar en el nombre del concurso
    const contestName = (contestData.contest_name || contestData.contest_id || '').trim();
    if (isSubsequence(searchValue, contestName)) return true;
    
    // Buscar en los códigos
    return contestData.codes.some(code => {
      const codeValue = (code.code || '').trim();
      return isSubsequence(searchValue, codeValue);
    });
  });

  if (loading) {
    return (
      <Container className="napc-page" maxWidth="xl" sx={{ py: 4, minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#6b7280' }}>
            Cargando...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container className="napc-page" maxWidth="xl" sx={{ py: { xs: 3, md: 6 }, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box>
          <Box className="napc-eyebrow">Access codes</Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#111827' }}>
            {t('buttonsforms.ListPassCodes') || 'Lista de Pass Codes'}
          </Typography>
        </Box>
        <Button
          onClick={fetchPassCodes}
          variant="outlined"
          startIcon={<RefreshCw className="w-4 h-4" />}
          sx={{ 
            minWidth: 'auto',
            px: 2,
            py: 1
          }}
        >
          {t('buttonsforms.Update')}
        </Button>
      </Box>

      {/* Content */}
      {contestPassCodes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Key className="w-16 h-16 text-gray-300 mb-4" style={{ margin: '0 auto' }} />
          <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
            No hay Pass Codes disponibles
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            Genera códigos de pase para un concurso para verlos aquí
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Buscador de Pass Codes */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder={t('buttonsforms.searchPlaceholder') || 'Buscar...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563eb',
                  },
                },
              }}
            />
          </Box>
          
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Allcontests') || 'Concurso'}</TableCell> 
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.Codes') || 'Códigos'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Activos</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Usados</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Disponibles</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>{t('menufilters.CreatedAt') || 'Creado'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContestPassCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                        {contestPassCodes.length === 0
                          ? 'No hay concursos con Pass Codes disponibles'
                          : 'No se encontraron Pass Codes que coincidan con la búsqueda'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContestPassCodes.map((contestData) => (
                    <TableRow key={contestData.contest_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {contestData.contest_name || contestData.contest_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {(expandedContest === contestData.contest_id ? contestData.codes : contestData.codes.slice(0, 3)).map((code) => (
                            <Box 
                              key={code.id}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                backgroundColor: code.used_at ? '#fee2e2' : code.is_active ? '#f3f4f6' : '#f9fafb',
                                padding: '4px 8px',
                                borderRadius: 1,
                                fontSize: '12px',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: code.used_at ? '#fecaca' : code.is_active ? '#e5e7eb' : '#f3f4f6'
                                }
                              }}
                              onClick={() => setSelectedCode(code)}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  flex: 1,
                                  textDecoration: code.used_at ? 'line-through' : 'none',
                                  color: code.used_at ? '#9ca3af' : '#111827'
                                }}
                              >
                                {code.code}
                              </Typography>
                              <Chip
                                label={code.used_at ? 'Usado' : code.is_active ? 'Activo' : 'Inactivo'}
                                color={code.used_at ? 'default' : code.is_active ? 'success' : 'default'}
                                size="small"
                                sx={{ height: '20px', fontSize: '10px' }}
                              />
                            </Box>
                          ))}
                          {contestData.codes.length > 3 && expandedContest !== contestData.contest_id && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#f97316',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                '&:hover': {
                                  color: '#ea580c',
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => setExpandedContest(contestData.contest_id)}
                            >
                              +{contestData.codes.length - 3} {t('buttonsforms.More') || 'más'}
                            </Typography>
                          )}
                          {expandedContest === contestData.contest_id && contestData.codes.length > 3 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#6b7280',
                                cursor: 'pointer',
                                '&:hover': {
                                  color: '#374151',
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => setExpandedContest(null)}
                            >
                              {t('buttonsforms.Less') || 'menos'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {contestData.total_codes}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contestData.active_codes}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contestData.used_codes}
                          color="default"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contestData.available_codes}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {contestData.codes.length > 0 
                            ? new Date(contestData.codes[0].created_at).toLocaleDateString()
                            : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Modal para mostrar detalles del código */}
      {selectedCode && (
        <Dialog 
          open={!!selectedCode} 
          onClose={() => setSelectedCode(null)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#2563eb', 
            color: 'white', 
            textAlign: 'center', 
            py: 2,
            m: 0,
            minHeight: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
              Detalles del Pass Code
            </Typography>
            <IconButton
              onClick={() => setSelectedCode(null)}
              sx={{ 
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                  Código
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 'bold', mt: 0.5 }}>
                  {selectedCode.code}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                    Estado
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedCode.used_at ? 'Usado' : selectedCode.is_active ? 'Activo' : 'Inactivo'}
                      color={selectedCode.used_at ? 'default' : selectedCode.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                    Usos Restantes
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {selectedCode.uses_remaining} / {selectedCode.max_uses}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                    Tipo
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {selectedCode.code_kind === 'printed' ? 'Impreso' : selectedCode.code_kind === 'digital' ? 'Digital' : 'NA'}
                  </Typography>
                </Box>
              </Box>
              
              {selectedCode.used_at && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                    Fecha de Uso
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {new Date(selectedCode.used_at).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {selectedCode.code_kind === 'digital' && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                    Envío digital
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedCode.sent_at
                      ? `Enviado a ${selectedCode.recipient_email || 'destinatario'} el ${new Date(selectedCode.sent_at).toLocaleString()}`
                      : 'Aún no enviado'}
                  </Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                  Fecha de Creación
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {new Date(selectedCode.created_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ justifyContent: 'center', p: 3, gap: 1 }}>
            <Button
              onClick={() => setSelectedCode(null)}
              variant="contained"
              sx={{ 
                backgroundColor: '#6b7280',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#4b5563'
                }
              }}
            >
              {t('buttonsforms.Close') || 'Cerrar'}
            </Button>
            {selectedCode.code_kind === 'digital' && (
              <Button
                onClick={() => {
                  const owner = contestPassCodes.find(cd => cd.codes.some(c => c.id === selectedCode.id));
                  setSendTarget({ code: selectedCode, contestName: owner?.contest_name || owner?.contest_id || '' });
                }}
                variant="contained"
                startIcon={<Send size={16} />}
                sx={{
                  backgroundColor: '#ED1790',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { backgroundColor: '#B01F8E' }
                }}
              >
                {selectedCode.sent_at ? 'Reenviar ticket' : 'Enviar ticket'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Diálogo para enviar ticket digital */}
      <SendDigitalCodeDialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        endpoint="passcodes"
        codeId={sendTarget?.code.id || null}
        codeValue={sendTarget?.code.code}
        contestName={sendTarget?.contestName}
        currentRecipient={sendTarget?.code.recipient_email}
        currentSentAt={sendTarget?.code.sent_at}
        onSent={({ codeId, recipient_email, sent_at }) => markCodeSent(codeId, recipient_email, sent_at)}
      />
    </Container>
  );
};

export default ContestPassCodesPage;

